import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { formValueSelector, reduxForm, InjectedFormProps } from 'redux-form'
// hooks
import { useAutoSso } from './hooks/useAutoSso'
import { usePasswordLogin, LoginFormData } from './hooks/usePasswordLogin'
// components
import { ReduxInput } from 'components/FormElements/ReduxForm'
import { InputAdornment } from '@mui/material'
import { Button, ErrorMessage, Link } from 'components/UI'
// view components
import SsoLoading from './components/SsoLoading'
import TwoFactorSetupModal from 'components/Modals/TwoFactorSetupModal'
import AuthCompanySelectModal from 'components/Modals/AuthCompanySelectModal'
// constants
import { Forms } from 'constants/forms'
import { selectAccountRequest } from 'providers/MicrosoftProvider/config'
// views
import { AuthForm, AuthHeader, ButtonWrapper, MicrosoftLoginButton } from 'themes/views/auth'
// static
import MicrosoftIcon from 'src/icons/ms.svg'
// utils
import { requiredEmpty } from 'utils/validators'

// Only `email` / `domain` are read (for the reset-password link) — select them
// individually so typing the password doesn't re-render the form.
const selectSignInValue = formValueSelector(Forms.SIGN_IN_FORM)

const SignIn: React.FC<InjectedFormProps<LoginFormData>> = ({ error, change, handleSubmit }) => {
  const router = useRouter()

  const { login } = usePasswordLogin()
  const { resolvingSso, skipSilentSso, microsoftSignIn } = useAutoSso()

  const email: string = useSelector((state) => selectSignInValue(state, 'email'))
  const domain: string = useSelector((state) => selectSignInValue(state, 'domain'))

  React.useEffect(() => {
    if (router.query.domain) {
      change('domain', decodeURI(router.query.domain as string))
    }
  }, [router.query])

  if (resolvingSso) {
    return <SsoLoading onUsePassword={skipSilentSso} />
  }

  return (
    <>
      <AuthHeader>LOGIN TO YOUR ACCOUNT</AuthHeader>
      <AuthForm onSubmit={handleSubmit(login)}>
        <ReduxInput
          type='text'
          name='domain'
          label='Domain'
          validate={requiredEmpty}
          endAdornment={<InputAdornment position='end'>.skefto.com</InputAdornment>}
        />
        <ReduxInput type='text' name='email' label='Email or Username' validate={[requiredEmpty]} />
        <div>
          <ReduxInput type='password' name='password' label='Password' validate={requiredEmpty} />
          <Link
            href={{
              pathname: '/reset-password',
              query: {
                ...(email && { email }),
                ...(domain && { domain })
              }
            }}>
            Forgot or change password
          </Link>
        </div>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        <ButtonWrapper>
          <Button variant='contained' fullWidth type='submit'>
            LOGIN
          </Button>
          <span />
          <Button variant='contained' fullWidth type='button' onClick={() => microsoftSignIn(selectAccountRequest)}>
            <MicrosoftLoginButton>
              <MicrosoftIcon />
              login with microsoft
            </MicrosoftLoginButton>
          </Button>
        </ButtonWrapper>
      </AuthForm>

      <TwoFactorSetupModal />
      <AuthCompanySelectModal />
    </>
  )
}

export default reduxForm<LoginFormData>({ form: Forms.SIGN_IN_FORM })(SignIn)
